import { useState, useRef, useEffect, useCallback, type JSX, type KeyboardEvent } from 'react'
import {
  Phone, PhoneOff, PhoneIncoming, Pause, Play, Mic, MicOff,
  ArrowRightLeft, Headphones, RefreshCw, Hash, Asterisk,
  Delete, RadioTower, Grid3X3, Settings, ChevronDown,
  User, UserPlus, Ticket, X,
} from 'lucide-react'
import { cn } from '../../utils/cn.js'
import type { CallState } from '../../modules/softphone/useSoftphone.js'
import { useSpeedDials } from '../../hooks/useAgentDirectory.js'
import { DispositionPicker } from './DispositionPicker.js'
import Select from '../ui/Select.js'

export interface AudioDevice {
  deviceId: string
  label: string
  kind: 'audioinput' | 'audiooutput'
}

export interface AgentSoftphonePanelProps {
  registered: boolean
  callState: CallState
  remoteCLI: string | null
  queueName?: string | null
  did?: string | null
  /** Resolved client-portal visitor name for the current call, when available. */
  callerDisplayName?: string | null
  /** Resolved client-portal visitor email for the current call, when available. */
  callerEmail?: string | null
  /**
   * The agent's portal status (Available / Break / Lunch / ...), mirrored from
   * agentStore.userStatus. Surfaced as the softphone's telephony status so the
   * panel reflects the state the ACD actually routes against — selecting
   * Break in the portal header must visibly change the phone, not just the
   * header dropdown.
   */
  agentStatus?: { key: string; text: string } | null
  isMuted: boolean
  callStartTime: number | null
  onDial: (digits: string) => void
  onAnswer: () => void
  onHangup: () => void
  onHold: () => void
  onUnhold: () => void
  /*
   * Opens the TransferDrawer — a right-side slide-over mounted at layout
   * level. It cannot live inside this panel: the floating softphone is
   * CSS-transformed (drag), which breaks `position: fixed` children.
   */
  onOpenTransfer?: () => void
  onClose?: () => void
  onMute: () => void
  onUnmute: () => void
  onSendDtmf: (tone: string) => boolean | void
  audioInputDevices: AudioDevice[]
  audioOutputDevices: AudioDevice[]
  selectedAudioInput: string
  selectedAudioOutput: string
  onSelectAudioInput: (id: string) => void
  onSelectAudioOutput: (id: string) => void
  onRefreshDevices: () => void
  onMakeAvailable: () => void
  canMakeAvailable: boolean
  echoTestPassed: boolean
  echoTestRunning: boolean
  /**
   * The last call ID — shown as a disposition picker during ACW.
   * Null when no call has happened or disposition is already submitted.
   */
  lastCallId?: string | null
  /** Tenant slug for the disposition API endpoint. */
  tenantSlug?: string
  /**
   * UI-4+5 — Recording controls.
   * When provided, the softphone shows pause/resume (PCI) and
   * on-demand start/stop recording buttons during an active call.
   */
  callId?: string | null
  channelId?: string | null
  bridgeId?: string | null
  agentId?: string | null
  allowAgentPause?: boolean
  recordingPaused?: boolean
  onRecordingPause?: (reason: 'PCI' | 'CONSENT') => void
  onRecordingResume?: () => void
  onRecordingStart?: () => void
  onRecordingStop?: () => void
  recordingActive?: boolean
  /**
   * UI-6 — Call waiting indicator.
   * True when a second incoming call arrives while the agent is already
   * on a call. Shows a blinking indicator prompting the agent to accept
   * or reject the waiting call.
   */
  callWaiting?: boolean
  onAcceptWaitingCall?: () => void
  onRejectWaitingCall?: () => void
  /**
   * Manual contact/ticket creation — replaces the auto screen-pop and the
   * auto-opened voice ticket. Opens the CallWorkDrawer (mounted by the
   * layout, outside this transformed panel) on the contact step / ticket
   * step respectively.
   */
  onCreateContact?: () => void
  onCreateTicket?: () => void
  /**
   * The caller's number already resolves to a `clients` row — the layout
   * looks it up via GET /agent/contacts/lookup. Hides "New Contact" (a saved
   * caller has nothing to create) and the panel shows their name via
   * `callerDisplayName` instead of the raw CLI.
   */
  callerIsClient?: boolean
}

const KEYS: Array<{ digit: string; label: string; icon?: typeof Asterisk; f1: number; f2: number }> = [
  { digit: '1', label: '', f1: 697, f2: 1209 },
  { digit: '2', label: 'ABC', f1: 697, f2: 1336 },
  { digit: '3', label: 'DEF', f1: 697, f2: 1477 },
  { digit: '4', label: 'GHI', f1: 770, f2: 1209 },
  { digit: '5', label: 'JKL', f1: 770, f2: 1336 },
  { digit: '6', label: 'MNO', f1: 770, f2: 1477 },
  { digit: '7', label: 'PQRS', f1: 852, f2: 1209 },
  { digit: '8', label: 'TUV', f1: 852, f2: 1336 },
  { digit: '9', label: 'WXYZ', f1: 852, f2: 1477 },
  { digit: '*', label: '', icon: Asterisk, f1: 941, f2: 1209 },
  { digit: '0', label: '+', f1: 941, f2: 1336 },
  { digit: '#', label: '', icon: Hash, f1: 941, f2: 1477 },
]

function fmtDur(ms: number): string {
  const s = Math.floor(ms / 1000)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${m}:${String(sec).padStart(2, '0')}`
}

function useTone() {
  const ctxRef = useRef<AudioContext | null>(null)

  // Ensure audio context exists and is running. Returns the context, or null
  // if it could not be created/resumed.
  const getCtx = useCallback((): AudioContext | null => {
    try {
      if (!ctxRef.current) ctxRef.current = new AudioContext()
      if (ctxRef.current.state === 'suspended') {
        // resume() returns a Promise; we don't await it here because getCtx
        // is called from synchronous call sites (DTMF, ring bursts). The
        // ringtone/ringback starters below DO await it before scheduling
        // their first burst.
        void ctxRef.current.resume()
      }
      return ctxRef.current
    } catch { /* ignore */ }
    return null
  }, [])

  // Play a clean dual-tone beep (DTMF) with smooth attack/decay
  const playDtmf = useCallback((f1: number, f2: number, dur = 0.12) => {
    try {
      const ctx = getCtx()
      if (!ctx) return
      const o1 = ctx.createOscillator()
      const o2 = ctx.createOscillator()
      const g = ctx.createGain()
      o1.type = 'sine'; o1.frequency.value = f1
      o2.type = 'sine'; o2.frequency.value = f2

      g.gain.setValueAtTime(0, ctx.currentTime)
      g.gain.linearRampToValueAtTime(0.06, ctx.currentTime + 0.015)
      g.gain.setValueAtTime(0.06, ctx.currentTime + dur - 0.025)
      g.gain.linearRampToValueAtTime(0, ctx.currentTime + dur)

      o1.connect(g); o2.connect(g); g.connect(ctx.destination)
      o1.start(ctx.currentTime)
      o2.start(ctx.currentTime)
      o1.stop(ctx.currentTime + dur)
      o2.stop(ctx.currentTime + dur)
    } catch { /* ignore */ }
  }, [getCtx])

  /*
   * Incoming ringtone and outgoing ringback are owned by useSoftphone.ts —
   * it plays them off the actual SIP session lifecycle (invite, 180 progress,
   * accepted, ended). This panel used to start a SECOND pair of oscillators
   * keyed on callState, so the agent heard two overlapping tones that
   * phased into an unbroken wail. Only DTMF keypress feedback lives here.
   */
  return { playDtmf }
}

export default function AgentSoftphonePanel(props: AgentSoftphonePanelProps): JSX.Element {
  const {
    registered, callState, remoteCLI, callerDisplayName, callerEmail, queueName, did, isMuted, callStartTime,
    agentStatus,
    onDial, onAnswer, onHangup, onHold, onUnhold, onOpenTransfer, onClose,
    onMute, onUnmute, onSendDtmf,
    audioInputDevices, audioOutputDevices,
    selectedAudioInput, selectedAudioOutput,
    onSelectAudioInput, onSelectAudioOutput, onRefreshDevices,
    onMakeAvailable, canMakeAvailable, echoTestPassed, echoTestRunning,
    lastCallId, tenantSlug,
    allowAgentPause = false,
    recordingPaused, onRecordingPause, onRecordingResume,
    onRecordingStart, onRecordingStop, recordingActive,
    callWaiting, onAcceptWaitingCall, onRejectWaitingCall,
    onCreateContact, onCreateTicket, callerIsClient,
  } = props

  /*
   * Telephony status — the portal status mirrored onto the phone.
   *
   * Color contract: emerald = routable (Available), blue = transitional
   * (Just Logged In), amber = not routable (Break/Lunch/...). During an
   * active call the call state takes visual precedence, so the status chip
   * only renders while the phone is idle.
   */
  const statusKey = agentStatus?.key ?? ''
  const telephonyStatus =
    agentStatus == null
      ? null
      : {
          text: agentStatus.text,
          textClass:
            statusKey === 'available'
              ? 'text-emerald-600'
              : statusKey === 'login'
                ? 'text-blue-600'
                : 'text-amber-600',
        }

  const [number, setNumber] = useState('')
  const [showSettings, setShowSettings] = useState(false)

  /*
   * Transfer moved OUT of this panel entirely — into TransferDrawer, a
   * right-side slide-over mounted at AgentLayout level. It cannot live
   * here: this floating panel is CSS-transformed for drag, which breaks
   * `position: fixed` children, and the 300px sidebar was too narrow for
   * the grouped destination list anyway. `onOpenTransfer` just opens it.
   */

  /*
   * Speed dials — the `speed_dial` table had no reader outside its admin
   * page, so an operator curated a directory no agent could see.
   */
  const [showSpeedDial, setShowSpeedDial] = useState(false)
  const [speedDialQuery, setSpeedDialQuery] = useState('')
  const speedDialRef = useRef<HTMLDivElement>(null)
  const speedDialSearchRef = useRef<HTMLInputElement>(null)
  const speedDials = useSpeedDials()

  /**
   * Filter on NAME or NUMBER.
   *
   * An agent looking for a speed dial knows one or the other, rarely both —
   * matching only the label would fail whenever they remember the extension.
   */
  const filteredSpeedDials = speedDials.items.filter((s) => {
    const q = speedDialQuery.trim().toLowerCase()
    if (!q) return true
    return s.name.toLowerCase().includes(q) || s.number.toLowerCase().includes(q)
  })

  // Close on outside click. The list is an OVERLAY, so without this it would
  // sit on top of the dial pad the agent is trying to use.
  useEffect(() => {
    if (!showSpeedDial) return
    const onDown = (e: MouseEvent): void => {
      if (!speedDialRef.current?.contains(e.target as Node)) setShowSpeedDial(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [showSpeedDial])

  // Focus the search on open so the agent can type straight away.
  useEffect(() => {
    if (showSpeedDial) speedDialSearchRef.current?.focus()
    else setSpeedDialQuery('')
  }, [showSpeedDial])
  const [showDtmf, setShowDtmf] = useState(false)
  const [dtmfDigits, setDtmfDigits] = useState('')
  const [dtmfError, setDtmfError] = useState<string | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const { playDtmf } = useTone()

  const inCall = callState === 'in_call' || callState === 'on_hold' || callState === 'ringing_out' || callState === 'ringing_in'
  const showDialpad = callState === 'idle' && echoTestPassed

  useEffect(() => {
    if (callState === 'ringing_out' || callState === 'in_call') setShowDtmf(true)
    if (callState === 'idle' || callState === 'ringing_in') {
      setShowDtmf(false)
      setDtmfDigits('')
    }
    setDtmfError(null)
  }, [callState])

  useEffect(() => {
    if (callStartTime && callState === 'in_call') {
      timerRef.current = setInterval(() => setElapsed(Date.now() - callStartTime), 1000)
    } else {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
      setElapsed(0)
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [callStartTime, callState])

  useEffect(() => { if (showDialpad && inputRef.current) inputRef.current.focus() }, [showDialpad])

  function handleDial() { if (registered && callState === 'idle' && number.trim()) { onDial(number.trim()); setNumber('') } }
  function bs() { setNumber((n) => n.slice(0, -1)) }
  function onKey(e: KeyboardEvent<HTMLInputElement>) { if (e.key === 'Enter') handleDial() }
  function addDigit(d: string, f1: number, f2: number) { playDtmf(f1, f2); setNumber((n) => n + d) }
  function dtmf(t: string, f1: number, f2: number) {
    if (echoTestRunning || (callState !== 'in_call' && callState !== 'ringing_out')) return
    try {
      if (onSendDtmf(t) === false) {
        setDtmfError('Could not send the digit. Wait for the call audio to connect, then try again.')
        return
      }
      playDtmf(f1, f2)
      setDtmfDigits((digits) => (digits + t).slice(-32))
      setDtmfError(null)
    } catch {
      setDtmfError('Could not send the digit. Wait for the call audio to connect, then try again.')
    }
  }
  function onDtmfKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.ctrlKey || e.altKey || e.metaKey) return
    const key = KEYS.find((k) => k.digit === e.key)
    if (!key) return
    e.preventDefault()
    if (!e.repeat) dtmf(key.digit, key.f1, key.f2)
  }

  const callLabel =
    callState === 'ringing_in' ? 'Incoming call'
      : callState === 'ringing_out' ? 'Calling…'
        : callState === 'on_hold' ? 'On hold'
          : 'In call'
  const callTone =
    callState === 'on_hold' ? 'amber' : callState === 'ringing_out' ? 'blue' : 'emerald'

  return (
    <div className="relative flex min-h-0 flex-1 flex-col bg-white text-slate-800 [&_button:focus-visible]:outline [&_button:focus-visible]:outline-2 [&_button:focus-visible]:outline-offset-2 [&_button:focus-visible]:outline-blue-600 [&_button:disabled]:cursor-not-allowed">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[3px] bg-slate-900 text-white">
            <Headphones className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-sm font-semibold tracking-tight text-slate-900">Softphone</h2>
            <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500" role="status">
              <span className={cn('h-1.5 w-1.5 rounded-[3px]', registered ? 'bg-emerald-500' : 'bg-amber-500')} />
              <span>{registered ? 'Connected' : 'Not registered'}</span>
              {telephonyStatus && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className={cn('truncate font-medium', telephonyStatus.textClass)}>
                    {telephonyStatus.text}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-0.5">
          <button type="button" aria-label="Audio settings" title="Audio settings" aria-expanded={showSettings}
            onClick={() => setShowSettings((s) => !s)}
            className={cn('flex h-9 w-9 items-center justify-center rounded-[3px] transition-colors', showSettings ? 'bg-blue-50 text-blue-700' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900')}>
            <Settings className="h-4 w-4" />
          </button>
          {onClose && (
            <button type="button" onClick={onClose} aria-label="Close softphone" title="Close softphone"
              className="flex h-9 w-9 items-center justify-center rounded-[3px] text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* telephony-container — the legacy dialer layout: a narrow icon
          rail on the left (.tp-sidebar) during a call, and the pad
          (.tp-content) on the right. */}
      <div className="flex min-h-0 flex-1">
        {/* tp-sidebar — in-call action rail. Old app order: Disconnect,
            Hold, Mute, Recording, Transfer, Keypad. */}
        {inCall && callState !== 'ringing_in' && !echoTestRunning && (
          <div className="flex w-[60px] shrink-0 flex-col items-center gap-2 overflow-y-auto border-r border-slate-100 bg-slate-50/80 py-4" role="group" aria-label="Call controls">
            <Btn onClick={() => { onHangup(); }} label="End" clr="redSolid">
              <PhoneOff className="h-3.5 w-3.5" />
            </Btn>
            {callState === 'on_hold' ? (
              <Btn onClick={() => { onUnhold(); }} label="Unhold" clr="emerald">
                <Play className="h-3.5 w-3.5" />
              </Btn>
            ) : (
              <Btn onClick={() => { onHold(); }} label="Hold" clr="amber">
                <Pause className="h-3.5 w-3.5" />
              </Btn>
            )}
            <Btn onClick={isMuted ? onUnmute : onMute} label={isMuted ? 'Unmute' : 'Mute'} clr={isMuted ? 'red' : 'gray'}>
              {isMuted ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
            </Btn>
            {allowAgentPause && onRecordingPause && onRecordingResume && (
              <Btn
                onClick={() => {
                  console.log('[AgentSoftphonePanel] Pause/Resume button clicked:', {
                    recordingPaused,
                    allowAgentPause,
                    recordingActive,
                    callState,
                  })
                  if (recordingPaused) {
                    onRecordingResume()
                  } else {
                    onRecordingPause('PCI')
                  }
                }}
                label={recordingPaused ? 'Resume' : 'Pause'}
                clr={recordingPaused ? 'emerald' : 'amber'}
              >
                {recordingPaused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
              </Btn>
            )}
            {!allowAgentPause && onRecordingStart && onRecordingStop && !recordingActive && (
              <Btn
                onClick={() => {
                  console.log('[AgentSoftphonePanel] Rec (on-demand) button clicked:', {
                    allowAgentPause,
                    recordingActive,
                    callState,
                  })
                  onRecordingStart()
                }}
                label="Rec"
                clr="gray"
              >
                <RadioTower className="h-3.5 w-3.5" />
              </Btn>
            )}
            {(callState === 'in_call' || callState === 'on_hold') && onOpenTransfer && (
              <Btn onClick={() => { onOpenTransfer(); }} label="Transfer" clr="blue">
                <ArrowRightLeft className="h-3.5 w-3.5" />
              </Btn>
            )}
            <Btn onClick={() => { setShowDtmf((s) => !s); }} label="Keypad" clr={showDtmf ? 'blue' : 'gray'}>
              <Grid3X3 className="h-3.5 w-3.5" />
            </Btn>
          </div>
        )}

        {/* tp-content */}
        <div className="flex min-w-0 flex-1 flex-col overflow-y-auto overscroll-contain px-4 py-5">

      {/* oncall-status — status line with the call timer on the right,
          like the legacy dialer's `.oncall-status` row */}
      {callState !== 'idle' && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-[3px] border border-slate-200 bg-slate-50 px-3 py-2.5">
          <span className="flex items-center gap-1.5">
            {callState === 'ringing_in' && <PhoneIncoming className="h-3 w-3 text-emerald-500 animate-bounce" />}
            {callState === 'ringing_out' && <Phone className="h-3 w-3 text-blue-500 animate-pulse" />}
            {callState === 'in_call' && <div className="h-2 w-2 rounded-[3px] bg-emerald-500" />}
            {callState === 'on_hold' && <Pause className="h-3 w-3 text-amber-500" />}
            <span className="text-[11px] font-semibold text-slate-700">
              {callState === 'ringing_in' && 'Incoming'}
              {callState === 'ringing_out' && 'Calling'}
              {callState === 'in_call' && 'In Call'}
              {callState === 'on_hold' && 'On Hold'}
            </span>
          </span>
          {callState === 'in_call' && (
            <span className="text-xs font-medium tabular-nums text-slate-700">
              <span className="mr-1 font-normal text-slate-400">Call Time</span>
              {fmtDur(elapsed)}
            </span>
          )}
        </div>
      )}

        {/* Incoming — same card as a live call; the contact-buttons row
            carries Answer / Decline instead of Dial / Disconnect. */}
        {callState === 'ringing_in' && (
          <div className="border-b border-slate-100 pb-3">
            <div className="flex justify-center pt-3">
              <div className="flex h-14 w-14 items-center justify-center rounded-[3px] border border-blue-100 bg-blue-50 text-blue-600">
                <User className="h-8 w-8 text-[#3366FF]" />
              </div>
            </div>
            <div className="mt-4 break-words pb-3 text-center text-xl font-semibold leading-snug tracking-tight text-slate-900">
              {callerDisplayName || remoteCLI || 'Unknown'}
            </div>
            <div className="mt-2 space-y-2 rounded-[3px] bg-slate-50 p-3 text-xs text-slate-600 [&>div]:gap-3 [&>div>span:last-child]:min-w-0 [&>div>span:last-child]:break-words [&>div>span:last-child]:text-right [&>div>span:last-child]:font-medium [&>div>span:last-child]:text-slate-900">
              {((remoteCLI && !/^visitor[-_]/.test(remoteCLI)) || callerDisplayName) && (
                <div className="flex justify-between">
                  <span>Caller</span>
                  <span>{remoteCLI && !/^visitor[-_]/.test(remoteCLI) ? remoteCLI : callerDisplayName}</span>
                </div>
              )}
              {callerEmail && (
                <div className="flex justify-between">
                  <span>Email</span>
                  <span>{callerEmail}</span>
                </div>
              )}
              {queueName && (
                <div className="flex justify-between">
                  <span>Queue</span>
                  <span>{queueName}</span>
                </div>
              )}
              {did && (
                <div className="flex justify-between">
                  <span>DID</span>
                  <span>{did}</span>
                </div>
              )}
            </div>
            <div className="mt-2 text-center text-[11px] font-semibold text-slate-600">INCOMING CALL</div>
            <div className="mt-5 flex gap-3">
              <button type="button" onClick={onAnswer} title="Answer"
                className="flex h-11 flex-1 items-center justify-center gap-2 rounded-[3px] bg-emerald-700 text-sm font-semibold text-white transition-colors hover:bg-emerald-800 active:bg-emerald-900">
                <Phone className="h-4 w-4" />Answer
              </button>
              <button type="button" onClick={onHangup} title="Decline"
                className="flex h-11 flex-1 items-center justify-center gap-2 rounded-[3px] border border-red-200 bg-red-50 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 active:bg-red-200">
                <PhoneOff className="h-4 w-4" />Decline
              </button>
            </div>
          </div>
        )}

        {/* Live call — control grid, keypad, call work, end */}
        {inCall && callState !== 'ringing_in' && (
          <>
            <div className="border-b border-slate-100 pb-3">
              {(recordingActive || (allowAgentPause && inCall)) && !recordingPaused && (
                <div className="mb-2 flex items-center justify-end gap-1.5 rounded-[3px] bg-red-50 px-2 py-1 text-xs font-semibold text-red-600">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600"></span>
                  </span>
                  <span>RECORDING</span>
                </div>
              )}
              {(recordingActive || (allowAgentPause && inCall)) && recordingPaused && (
                <div className="mb-2 flex flex-col items-end gap-0.5 rounded-[3px] border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-right">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-amber-800">
                    <Pause className="h-3 w-3 text-amber-600" />
                    <span>PAUSED</span>
                  </div>
                  <div className="text-[10px] font-medium text-amber-700">
                    Audio is currently not being recorded
                  </div>
                </div>
              )}
              <div className="flex justify-center pt-2">
                <div className="flex h-14 w-14 items-center justify-center rounded-[3px] border border-blue-100 bg-blue-50 text-blue-600">
                  <User className="h-8 w-8 text-[#3366FF]" />
                </div>
              </div>
              <div className="mt-4 break-words pb-3 text-center text-xl font-semibold leading-snug tracking-tight text-slate-900">
                {callerDisplayName || remoteCLI || 'Unknown'}
              </div>
              <div className="mt-2 space-y-2 rounded-[3px] bg-slate-50 p-3 text-xs text-slate-600 [&>div]:gap-3 [&>div>span:last-child]:min-w-0 [&>div>span:last-child]:break-words [&>div>span:last-child]:text-right [&>div>span:last-child]:font-medium [&>div>span:last-child]:text-slate-900">
                {((remoteCLI && !/^visitor[-_]/.test(remoteCLI)) || callerDisplayName) && (
                  <div className="flex justify-between">
                    <span>Caller</span>
                    <span>{remoteCLI && !/^visitor[-_]/.test(remoteCLI) ? remoteCLI : callerDisplayName}</span>
                  </div>
                )}
                {callerEmail && (
                  <div className="flex justify-between">
                    <span>Email</span>
                    <span>{callerEmail}</span>
                  </div>
                )}
                {queueName && (
                  <div className="flex justify-between">
                    <span>Queue</span>
                    <span>{queueName}</span>
                  </div>
                )}
                {did && (
                  <div className="flex justify-between">
                    <span>DID</span>
                    <span>{did}</span>
                  </div>
                )}
              </div>

              {/* End call — placed directly below the caller and echo test row */}
              <div className="mt-4">
                <button type="button" onClick={onHangup} title="Disconnect"
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-[3px] bg-red-600 text-sm font-semibold text-white shadow-[0_8px_20px_-8px_rgba(220,38,38,0.6)] transition-colors hover:bg-red-700 active:bg-red-800">
                  <PhoneOff className="h-4 w-4" />End call
                </button>
              </div>

              {/* Manual call-work actions — Create Contact / Create Ticket.
                  Only while a call is live; "New Contact" only for callers
                  that are not already a saved client. Drawers mount in
                  AgentLayout (this panel is transformed). */}
              {!echoTestRunning && (onCreateContact || onCreateTicket) && (
                <div className="mt-3 flex gap-2">
                  {onCreateContact && !callerIsClient && (
                    <button
                      onClick={onCreateContact}
                      className="flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded-[3px] border border-slate-200 bg-white px-2 py-2 text-xs font-medium text-slate-700 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                    >
                      <UserPlus className="h-3 w-3" />
                      New Contact
                    </button>
                  )}
                  {onCreateTicket && (
                    <button
                      onClick={onCreateTicket}
                      className="flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded-[3px] border border-slate-200 bg-white px-2 py-2 text-xs font-medium text-slate-700 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                    >
                      <Ticket className="h-3 w-3" />
                      New Ticket
                    </button>
                  )}
                </div>
              )}
            </div>

            {callWaiting && (
              <div className="mt-4 flex items-center justify-between gap-2 rounded-[3px] border border-amber-200 bg-amber-50/70 px-3 py-2.5">
                <div className="flex items-center gap-2 text-xs font-semibold text-amber-800">
                  <span className="inline-block h-2 w-2 animate-pulse rounded-[3px] bg-amber-500" />
                  Incoming call waiting
                </div>
                {onAcceptWaitingCall && onRejectWaitingCall && (
                  <div className="flex gap-1.5">
                    <button
                      onClick={onRejectWaitingCall}
                      className="rounded-[3px] px-2.5 py-1 text-[11px] font-semibold text-red-600 hover:bg-red-50"
                    >
                      Reject
                    </button>
                    <button
                      onClick={onAcceptWaitingCall}
                      className="rounded-[3px] bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700"
                    >
                      Accept
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* DTMF — keypad behind the Keypad control, mid-call only */}
            {showDtmf && inCall && !echoTestRunning && (
              <div className="my-4" role="group" aria-label="In-call keypad" onKeyDown={onDtmfKey}>
                <input type="text" inputMode="tel" aria-label="DTMF digits" readOnly value={dtmfDigits}
                  placeholder="Press a key for the IVR" autoComplete="off"
                  className="mb-3 h-10 w-full rounded-[3px] border border-slate-200 bg-slate-50 px-3 text-center text-sm tabular-nums tracking-wider outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
                {callState === 'on_hold' && <p className="mb-2 text-xs text-amber-700">Resume the call to send keypad digits.</p>}
                {dtmfError && <p role="alert" className="mb-2 text-xs text-red-700">{dtmfError}</p>}
                <div className="grid grid-cols-3 gap-2">
                  {KEYS.map((k) => {
                    const Icon = k.icon
                    return (
                      <button type="button" key={k.digit} aria-label={k.digit} disabled={callState === 'on_hold'} onClick={() => dtmf(k.digit, k.f1, k.f2)}
                        className="flex h-12 items-center justify-center rounded-[3px] border border-slate-200 bg-white text-lg font-medium text-slate-800 transition-colors hover:border-blue-300 hover:bg-blue-50 active:bg-blue-100 disabled:opacity-40">
                        {Icon ? <Icon className="h-4 w-4 text-slate-500" /> : k.digit}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* Dialpad */}
        {/* SPEED DIAL — `speed_dial` is a curated per-tenant table with an admin
            page and, until now, no reader anywhere outside it.

            RENDERED OUTSIDE `showDialpad` DELIBERATELY. That flag requires
            `echoTestPassed`, so putting the list inside it meant an agent who
            had not yet run the device echo test saw no speed dials — a device
            check gating a read-only address list.

            The section is ALWAYS rendered when not in a call, including when
            the list is empty. Hiding it on empty is what made this
            indistinguishable from "the feature is broken": with nothing on
            screen there is no way to tell an unconfigured tenant from a failed
            request, which is the exact silent-absence pathology this work
            exists to remove. */}
        {!inCall && (
          /* `relative` anchors the overlay; the list itself is absolutely
             positioned so opening it does NOT reflow the dial pad below. */
          <div className="relative mb-2" ref={speedDialRef}>
            <button
              onClick={() => setShowSpeedDial((s) => !s)}
              aria-expanded={showSpeedDial}
              className="flex min-h-10 w-full items-center justify-between rounded-[3px] border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50"
            >
              <span>Speed dial ({speedDials.items.length})</span>
              <ChevronDown className={cn('h-3.5 w-3.5 text-slate-400 transition-transform', showSpeedDial && 'rotate-180')} />
            </button>

            {showSpeedDial && (
              /* Opens DOWNWARD, directly under its trigger — the ordinary
                 dropdown direction, and where the eye already is after
                 clicking.

                 ABSOLUTELY POSITIONED, which is the part that matters: an
                 in-flow list pushed the dial pad and Call button down the
                 panel every time it opened, so the keypad moved under the
                 agent's cursor. As an overlay it floats over that content and
                 nothing below it shifts. */
              <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-[3px] border border-slate-200 bg-white shadow-lg">
                <input
                  ref={speedDialSearchRef}
                  type="text"
                  value={speedDialQuery}
                  onChange={(e) => setSpeedDialQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') { e.preventDefault(); setShowSpeedDial(false) }
                    // Enter dials the only remaining match — the common case
                    // after typing two or three characters.
                    if (e.key === 'Enter' && filteredSpeedDials.length === 1 && registered) {
                      const only = filteredSpeedDials[0]
                      if (only) { onDial(only.number); setNumber(''); setShowSpeedDial(false) }
                    }
                  }}
                  placeholder="Search name or number…"
                  className="w-full border-b border-slate-200 px-2.5 py-1.5 text-[11px] outline-none focus:border-blue-400"
                />

                <div className="max-h-52 overflow-y-auto py-1">
                  {speedDials.loading && (
                    <p className="px-2.5 py-2 text-[11px] text-slate-500">Loading…</p>
                  )}
                  {/* Say WHICH of the four it is — loading, failed, none
                      configured, or none matching the search. */}
                  {!speedDials.loading && speedDials.error && (
                    <p className="px-3 py-2 text-[11px] text-red-600">
                      Could not load speed dials — {speedDials.error}
                    </p>
                  )}
                  {!speedDials.loading && !speedDials.error && speedDials.items.length === 0 && (
                    <p className="px-2.5 py-2 text-[11px] text-slate-500">
                      No speed dials configured. An admin adds them under
                      Telephony Management → Speed Dial.
                    </p>
                  )}
                  {!speedDials.loading && speedDials.items.length > 0 && filteredSpeedDials.length === 0 && (
                    <p className="px-2.5 py-2 text-[11px] text-slate-500">
                      No match for “{speedDialQuery}”.
                    </p>
                  )}

                  {filteredSpeedDials.map((s) => (
                    <div key={s.id} className="flex items-center gap-1 border-b border-slate-100 last:border-b-0">
                      {/* Two actions: LOAD into the input (so the agent can
                          check it before dialling) and CALL directly. */}
                      <button
                        onClick={() => { setNumber(s.number); setShowSpeedDial(false) }}
                        className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded-[3px] px-2 py-1.5 text-left text-xs hover:bg-slate-50"
                        title="Load into the dial pad"
                      >
                        <span className="truncate font-medium text-slate-800">{s.name}</span>
                        <span className="shrink-0 text-slate-500">{s.number}</span>
                      </button>
                      <button
                        onClick={() => { onDial(s.number); setNumber(''); setShowSpeedDial(false) }}
                        disabled={!registered}
                        title={registered ? `Call ${s.name}` : 'Softphone is not registered'}
                        className="shrink-0 rounded-[3px] p-1.5 text-emerald-600 transition-colors hover:bg-emerald-50 disabled:opacity-40"
                      >
                        <Phone className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {showDialpad && (
          <>
            <div className="relative mb-4 mt-3">
              <input ref={inputRef} type="text" inputMode="tel" aria-label="Phone number" autoComplete="off" value={number} onChange={(e) => setNumber(e.target.value)} onKeyDown={onKey}
                className={cn(
                  "h-16 w-full border-0 border-b border-slate-200 bg-transparent text-center font-sans font-light tabular-nums text-slate-900 outline-none transition-colors placeholder:text-base placeholder:font-normal placeholder:tracking-normal placeholder:text-slate-300 focus:border-[#3366FF]",
                  number.length > 0 ? "pl-2 pr-8" : "px-2",
                  number.length > 13 ? "text-[23px] tracking-tight" : number.length > 10 ? "text-[25px] tracking-tight" : "text-[28px] tracking-normal",
                )}
                placeholder="Enter number" />
              {number.length > 0 && (
                <button type="button" onClick={bs} aria-label="Delete last digit"
                  className="absolute right-0 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-[3px] text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-700">
                  <Delete className="h-4 w-4" />
                </button>
              )}
            </div>
            <div role="group" aria-label="Dial keypad">
              <Keypad onPress={(k) => addDigit(k.digit, k.f1, k.f2)} />
            </div>
            <button onClick={() => { handleDial(); }}
              disabled={!number.trim() || !registered || callState !== 'idle'}
              title={
                !registered
                  ? 'Cannot call — the softphone is not registered with the SIP server.'
                  : !number.trim()
                    ? 'Enter a number to call.'
                    : 'Place call'
              }
              className="mt-4 flex h-11 w-full shrink-0 items-center justify-center gap-2 rounded-[3px] bg-emerald-700 text-sm font-semibold text-white transition-colors hover:bg-emerald-800 active:bg-emerald-900 disabled:bg-slate-100 disabled:text-slate-400">
              <Phone className="h-4 w-4" />Call
            </button>
            {/*
              Say WHY the button is dead.
              Without this, an unregistered softphone presents as a Call button
              that simply never enables: the agent types a valid number and
              nothing happens, with no error anywhere in the UI. The status dot
              further down does read "SIP Disconnected", but it is a 10px label
              at the bottom of the panel and reads as ambient status rather than
              the cause. Every report of this reached us as "the call button is
              broken", never as "the softphone is not registered" — so the
              explanation belongs next to the control that looks broken.
            */}
            {!registered && (
              <p className="mt-2 text-center text-[11px] leading-snug text-red-500">
                Softphone not registered — calls are unavailable.
              </p>
            )}
          </>
        )}

        {/* Disposition picker — shown during ACW after a call ends */}
        {callState === 'idle' && lastCallId && tenantSlug && (
          <DispositionPicker callId={lastCallId} tenantSlug={tenantSlug} />
        )}

        {/* Setup cards */}
        {callState === 'idle' && !echoTestPassed && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-4">
            {canMakeAvailable && (
              <div className="w-full rounded-[3px] border border-slate-200 bg-white p-4 text-center shadow-sm">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-[3px] bg-blue-50">
                  <RadioTower className="h-6 w-6 text-[#3366FF]" />
                </div>
                <div className="text-sm font-bold text-slate-800">Set Available</div>
                <div className="mb-3 text-[11px] text-slate-500">Start receiving calls</div>
                <button onClick={() => { onMakeAvailable(); }}
                  className="mt-5 flex h-10 w-full items-center justify-center gap-2 rounded-[3px] bg-[#3366FF] text-xs font-semibold text-white transition-colors hover:bg-[#2952CC]">
                  <RadioTower className="h-4 w-4" />Make Available
                </button>
              </div>
            )}
            {!canMakeAvailable && (
              <div className="w-full rounded-[3px] border border-slate-200 bg-white p-4 text-center shadow-sm">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-[3px] bg-amber-50">
                  <Headphones className="h-6 w-6 text-amber-500" />
                </div>
                <div className="text-sm font-bold text-slate-800">Audio Check</div>
                <div className="text-[11px] text-slate-500">Complete to start calls</div>
              </div>
            )}
          </div>
        )}

        {/* Settings */}
        {showSettings && (
          <div className="mt-5 space-y-3 rounded-[3px] border border-slate-200 bg-slate-50/60 p-3" role="region" aria-label="Audio devices">
            <div className="flex items-center gap-2 rounded-[3px] bg-slate-50 px-3 py-2">
              <div className={cn('h-2 w-2 rounded-[3px]', registered ? 'bg-emerald-500' : 'bg-red-400')} />
              <span className="text-[10px] text-slate-600">{registered ? 'SIP Registered' : 'SIP Disconnected'}</span>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-600">Microphone</label>
              <Select ariaLabel="Microphone" size="sm" value={selectedAudioInput} onChange={onSelectAudioInput}>
                {audioInputDevices.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)}
                {audioInputDevices.length === 0 && <option value="">None</option>}
              </Select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-600">Speaker</label>
              <Select ariaLabel="Speaker" size="sm" value={selectedAudioOutput} onChange={onSelectAudioOutput}>
                {audioOutputDevices.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)}
                {audioOutputDevices.length === 0 && <option value="">None</option>}
              </Select>
            </div>
            <button onClick={onRefreshDevices}
              className="flex w-full items-center justify-center gap-1.5 rounded-[3px] border border-slate-200 bg-white px-2 py-1.5 text-[10px] text-slate-600 hover:bg-slate-50">
              <RefreshCw className="h-3 w-3" />Refresh
            </button>
          </div>
        )}
      </div>
    </div>
    </div>
  )
}

/* Dial / DTMF keypad — borderless keys, light numerals */
function Keypad({ onPress, disabled, compact }: {
  onPress: (key: (typeof KEYS)[number]) => void
  disabled?: boolean | undefined
  compact?: boolean | undefined
}) {
  return (
    <div className="grid grid-cols-3 gap-1.5">
      {KEYS.map((k) => {
        const Icon = k.icon
        return (
          <button type="button" key={k.digit} aria-label={k.digit} disabled={disabled} onClick={() => onPress(k)}
            className={cn(
              'flex flex-col items-center justify-center rounded-[3px] text-slate-900 transition-colors hover:bg-slate-50 active:bg-slate-100 disabled:opacity-40',
              compact ? 'h-12' : 'h-[60px]',
            )}>
            {Icon ? <Icon className="h-4 w-4 text-slate-500" /> : (
              <>
                <span className={cn('font-light leading-none', compact ? 'text-xl' : 'text-[26px]')}>{k.digit}</span>
                {k.label && !compact && <span className="mt-1 text-[9px] font-semibold uppercase leading-none tracking-[0.2em] text-slate-400">{k.label}</span>}
              </>
            )}
          </button>
        )
      })}
    </div>
  )
}

/* Caller identity — monogram, name and labelled detail rows */
function CallerCard({ name, rows, ringing }: {
  name: string
  rows: Array<[string, string] | null>
  ringing?: boolean | undefined
}) {
  const initials = /[a-z]/i.test(name)
    ? name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('')
    : ''
  const details = rows.filter((r): r is [string, string] => r != null)
  return (
    <div className="text-center">
      <div className="relative mx-auto h-16 w-16">
        {ringing && <span className="absolute inset-0 animate-ping rounded-[3px] bg-emerald-200/60" />}
        <div className="relative flex h-16 w-16 items-center justify-center rounded-[3px] bg-gradient-to-br from-slate-800 to-slate-950 text-white shadow-[0_10px_24px_-12px_rgba(15,23,42,0.7)]">
          {initials ? <span className="text-lg font-semibold tracking-wide">{initials}</span> : <User className="h-7 w-7 text-white/90" />}
        </div>
      </div>
      <div className="mt-4 break-words text-lg font-semibold leading-snug tracking-tight text-slate-900">{name}</div>
      {details.length > 0 && (
        <dl className="mt-4 divide-y divide-slate-100 border-y border-slate-100 text-left text-xs">
          {details.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-3 py-2">
              <dt className="shrink-0 text-slate-400">{label}</dt>
              <dd className="min-w-0 break-words text-right font-medium text-slate-800">{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}

/* Control button */
function Btn({ onClick, label, clr, children, title }: {
  onClick: () => void
  label: string
  clr: 'emerald' | 'amber' | 'red' | 'blue' | 'gray' | 'redSolid'
  children: React.ReactNode
  title?: string
}) {
  const styles: Record<string, string> = {
    emerald: 'border-emerald-100 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
    amber: 'border-amber-100 bg-amber-50 text-amber-800 hover:bg-amber-100',
    red: 'border-red-100 bg-red-50 text-red-700 hover:bg-red-100',
    blue: 'border-blue-100 bg-blue-50 text-blue-700 hover:bg-blue-100',
    gray: 'border-transparent text-slate-600 hover:border-slate-200 hover:bg-white',
    redSolid: 'border-red-100 bg-red-50 text-red-700 hover:bg-red-100',
  }
  return (
    <button type="button" onClick={onClick} title={title ?? label}
      className={cn('flex min-h-12 w-12 shrink-0 flex-col items-center justify-center gap-1 rounded-[3px] border px-0.5 py-2 transition-colors', styles[clr])}>
      {children}
      <span className="text-[10px] font-medium leading-none">{label}</span>
    </button>
  )
}
